import { BankDetail } from '@sr-sdks/api-sdk-axios';
import {
  Alert,
  Dialog,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyIcon,
  EmptyMedia,
  EmptyTitle,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { queryOptionsGetTitlesPaginated } from '../../../api/titles/titlesApi';
import { queryOptionsGetClientsPaginated } from '../../../api/workspaces/clientApi';
import { BankAccountCardV3 } from '../../../components/BankAccountCardV3';
import BankDetailSideOverlay from '../../../components/BankDetails/BankDetailSideOverlay';
import IfFeatureEnabledMSR3 from '../../../components/IfFeatureEnabledMSR3';
import AppLoader from '../../../components/Loader';
import { ErrorFallback } from '../../../components/QueryResults/ErrorFallback';
import { BankDetailsSkeleton } from '../../../components/skeletons/BankDetailsSkeleton';
import { FEATURE_FLAGS } from '../../../constant/featureFlags';
import { useLoadingToast } from '../../../hooks/useLoadingToast';
import { useVisibleBankDetails } from '../../../hooks/useVisibleBankDetails';
import { bankDetailsService } from '../../../reducers/payments/bankDetailSlice';
import { paymentRuleService } from '../../../reducers/payments/paymentRuleSlice';
import { reduxServices } from '../../../reducers/reduxServices';
import { useAppDispatch, useTypedSelector } from '../../../stateStore';
import { WorkspaceOutletContext } from '../../../types/workspaceTypes';
import {
  isFailedState,
  isLoadingState,
  isSuccessState,
} from '../../../utils/helpers/common';

const CARD_GRID_CLASS_NAME = 'grid gap-4';

export const BankAccount = () => {
  const { workspaceId } = useOutletContext<WorkspaceOutletContext>();
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBankDetail, setSelectedBankDetail] = useState<BankDetail>();

  const {
    bankDetails,
    canAddBankAccount,
    isMultiBankFlagLoading,
    isMultiBankFlagUnavailable,
    visibleBankDetails,
  } = useVisibleBankDetails();
  const isLoading = useTypedSelector(bankDetailsService.selectors.isLoading);
  const isBankDetailsCreating = useTypedSelector(
    bankDetailsService.selectors.isCreating,
  );
  const isBankDetailsUpdating = useTypedSelector(
    bankDetailsService.selectors.isUpdating,
  );
  const isBankDetailsRemoving = useTypedSelector(
    bankDetailsService.selectors.isRemoving,
  );
  const isPaymentRuleCreating = useTypedSelector(
    paymentRuleService.selectors.isCreating,
  );
  const isPaymentRuleUpdating = useTypedSelector(
    paymentRuleService.selectors.isUpdating,
  );
  const isPaymentRuleRemoveLoading = useTypedSelector(
    paymentRuleService.selectors.isRemoving,
  );
  const isSetDefaulting = useTypedSelector(
    bankDetailsService.selectors.isSetDefaulting,
  );

  const handleEditBankAccount = (bankDetailsId: string) => {
    setSearchParams(
      (current) => {
        current.set('bankAccountForm', bankDetailsId);

        return current;
      },
      { replace: true },
    );
  };

  const handleDeleteBankAccount = (bankDetail: BankDetail) => {
    setSelectedBankDetail(bankDetail);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBankDetail || !workspaceId) return;

    const deleteBankDetailsResult = await dispatch(
      reduxServices.bankDetails.remove(selectedBankDetail._id),
    );

    if (
      reduxServices.bankDetails.remove.fulfilled.match(deleteBankDetailsResult)
    ) {
      dispatch(
        bankDetailsService.findAll({
          sortBy: 'isDefault:desc',
          workspaceId,
        }),
      );
      setShowDeleteModal(false);
      setSelectedBankDetail(undefined);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedBankDetail(undefined);
  };

  // Fetch clients and titles for use by BankAccountCardV3 instances
  useQuery(
    queryOptionsGetClientsPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId,
    }),
  );

  useQuery(
    queryOptionsGetTitlesPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId,
    }),
  );

  useEffect(() => {
    if (!workspaceId) return;

    dispatch(
      bankDetailsService.findAll({
        sortBy: 'isDefault:desc',
        workspaceId,
      }),
    );
  }, [dispatch, workspaceId]);

  useLoadingToast(
    isBankDetailsCreating,
    'A new bank account has been created.',
    bankDetailsService.actions.resetCreatingLoadingState,
  );
  useLoadingToast(
    isBankDetailsUpdating,
    'Your bank account has been updated.',
    bankDetailsService.actions.resetUpdatingLoadingState,
  );
  useLoadingToast(
    isBankDetailsRemoving,
    'Your bank account has been deleted.',
    bankDetailsService.actions.resetRemovingLoadingState,
  );
  useLoadingToast(
    isSetDefaulting,
    'Your default bank account has been switched.',
    bankDetailsService.actions.resetSetDefaultLoadingState,
  );
  // Payment rule create/update failures are surfaced by the form itself
  // (duplicate -> dialog, other errors -> toast), so no failureMessage here.
  useLoadingToast(
    isPaymentRuleCreating,
    'Your bank account payment rule has been created.',
    paymentRuleService.actions.resetCreatingLoadingState,
  );
  useLoadingToast(
    isPaymentRuleUpdating,
    'Your bank account payment rule has been updated.',
    paymentRuleService.actions.resetUpdateLoadingState,
  );
  useLoadingToast(
    isPaymentRuleRemoveLoading,
    'Your bank account payment rule has been removed.',
    paymentRuleService.actions.resetRemoveLoadingState,
  );

  // The bank-details fetch has four states, not two: `idle` groups with
  // `loading` here because `findAll` has not resolved either way, so the
  // account count is unknown -- rendering the empty state or the cards would
  // assert a fact this hasn't confirmed. `failed` is handled below, alongside
  // the flag-unavailable branch, so a page where both requests failed shows
  // one error rather than two.
  if (!isFailedState(isLoading) && !isSuccessState(isLoading)) {
    return <AppLoader.FlexLoader />;
  }

  // Neither variant of the flag-gated content may paint before the flag is
  // known, so both unresolved states return early, above `IfFeatureEnabledMSR3`.
  if (isMultiBankFlagLoading) {
    return (
      <BankDetailSideOverlay canAddBankAccount={canAddBankAccount}>
        <BankDetailsSkeleton
          cardCount={bankDetails.length}
          cardGridClassName={CARD_GRID_CLASS_NAME}
        />
      </BankDetailSideOverlay>
    );
  }

  // Terminal branch. It deliberately mounts nothing that observes the features
  // query: `IfFeatureEnabledMSR3` calls `useFeatureFlag` again, and a second
  // observer on a query that has never succeeded starts a fresh fetch, which
  // flips this page back to its loading branch and unmounts that observer —
  // the cycle that produced ~1,000 requests a second to `/setting/feature`.
  // `ErrorFallback` only renders an Alert, so it's safe here -- and it also
  // covers a failed bank-details fetch, so both failures still surface as one
  // error rather than two stacked alerts.
  if (isMultiBankFlagUnavailable || isFailedState(isLoading)) {
    return (
      <BankDetailSideOverlay canAddBankAccount={canAddBankAccount}>
        <ErrorFallback />
      </BankDetailSideOverlay>
    );
  }

  return (
    <BankDetailSideOverlay canAddBankAccount={canAddBankAccount}>
      <div className="space-y-6">
        <IfFeatureEnabledMSR3
          name={FEATURE_FLAGS.MULTI_BANK_ACCOUNT}
          isEnabled="false"
        >
          <h2 className="text-xl font-semibold text-neutral-950">
            My Bank Account
          </h2>
        </IfFeatureEnabledMSR3>

        <IfFeatureEnabledMSR3 name={FEATURE_FLAGS.MULTI_BANK_ACCOUNT}>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-neutral-950">
              My Bank Accounts
            </h2>

            <Alert variant="info">
              <div className="space-y-2 text-sm">
                <p>
                  All future payments will be directed to the
                  &apos;Default&apos; bank account.
                </p>
                <p>
                  To make payments to specific bank accounts based on criteria
                  such as title, client, or service, please{' '}
                  <strong>add a new bank account</strong> and configure a{' '}
                  <strong>payment rule.</strong>
                </p>
              </div>
            </Alert>
          </div>
        </IfFeatureEnabledMSR3>

        {/* Reaching this point means the bank-details fetch has succeeded (the
            guard clauses above return early on idle/loading/failed), so an
            empty `bankDetails` here is a confirmed zero, not an unknown. */}
        {bankDetails.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia>
                <EmptyIcon />
              </EmptyMedia>
              <EmptyTitle>There are no bank accounts</EmptyTitle>
              <EmptyDescription>
                Added bank accounts will be displayed here.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className={CARD_GRID_CLASS_NAME}>
            {visibleBankDetails.map((bankDetail) => (
              <BankAccountCardV3
                key={`${bankDetail._id}-${bankDetail.workspaceId}`}
                bankDetail={bankDetail}
                onDeleteAccountClick={handleDeleteBankAccount}
                onEditAccountClick={handleEditBankAccount}
              />
            ))}
          </div>
        )}

        <Dialog
          heading="Are you sure you want to delete this bank account?"
          actionButtonProps={{
            children: 'Delete',
            loading: isLoadingState(isBankDetailsRemoving),
            onClick: handleConfirmDelete,
          }}
          cancelButtonProps={{
            onClick: handleDeleteCancel,
          }}
          open={showDeleteModal}
          onOpenChange={handleDeleteCancel}
        >
          <p>
            Any payment rules attached to this account will also be deleted.
          </p>
        </Dialog>
      </div>
    </BankDetailSideOverlay>
  );
};
